import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const alertVariants = cva(
  "relative w-full rounded-lg border overflow-hidden [&>div:last-child>svg~*]:pl-7 [&>div:last-child>svg+div]:translate-y-[-3px] [&>div:last-child>svg]:absolute [&>div:last-child>svg]:left-4 [&>div:last-child>svg]:top-4 [&>div:last-child>svg]:text-foreground",
  {
    variants: {
      variant: {
        default: "bg-background text-foreground",
        destructive:
          "border-destructive/50 text-destructive dark:border-destructive [&>div:last-child>svg]:text-destructive",
        warning:
          "text-[#b45309] bg-[#fffbeb]",
        success:
          "text-[#047857] bg-[#ecfdf5]",
        nochange:
          "text-thalo-blue bg-[#eff6ff]",
        allgreen:
          "text-[#047857] bg-[#ecfdf5]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const alertBorderVariants = cva(
  "w-[6px] shrink-0",
  {
    variants: {
      variant: {
        default: "hidden",
        destructive: "hidden",
        warning: "bg-[#b45309]",
        success: "bg-[#047857]",
        nochange: "bg-thalo-blue",
        allgreen: "bg-[#047857]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, children, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ variant }), "flex flex-row", className)}
    {...props}
  >
    <div className={cn(alertBorderVariants({ variant }))} />
    <div className="grow p-4">
      {children}
    </div>
  </div>
));
Alert.displayName = "Alert";

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn("mb-1 font-medium leading-none tracking-tight", className)}
    {...props}
  />
));
AlertTitle.displayName = "AlertTitle";

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm [&_p]:leading-relaxed", className)}
    {...props}
  />
));
AlertDescription.displayName = "AlertDescription";

export { Alert, AlertTitle, AlertDescription };
